// Language-neutral verb-group data shared by Lesson 11.5 (LessonsPage), and the
// Godan vs Ichidan blog posts (English and PT-PT). The few English strings
// here (form labels, meanings, prose) are the English source: the lesson
// translates them through its PT bundle, the PT blog supplies its own.
//
// The kana in each form that moves between steps is kept separate
// (stem + kana + ending) so tables can highlight just that kana.
import type { TableCellValue } from "../components/ui/TableCell/TableCell";

// stem + the kana that moves + the fixed ending, as a table cell where only the
// moving kana is emphasised: 売 + ら + ない -> 売<strong>ら</strong>ない.
export function stepCell(stem: string, kana: string, ending = ""): TableCellValue {
    return [stem, { strong: kana }, ending].filter((part) => part !== "");
}

export const godanFiveSteps = [
    { step: "あ段", vowel: "a", stem: "売", kana: "ら", ending: "ない", reading: "うらない", romaji: "uranai", use: "Plain negative (～ない)" },
    { step: "い段", vowel: "i", stem: "売", kana: "り", ending: "ます", reading: "うります", romaji: "urimasu", use: "Polite form (～ます)" },
    { step: "う段", vowel: "u", stem: "売", kana: "る", ending: "", reading: "うる", romaji: "uru", use: "Dictionary form" },
    { step: "え段", vowel: "e", stem: "売", kana: "れ", ending: "ば", reading: "うれば", romaji: "ureba", use: "Conditional (～ば)" },
    { step: "お段", vowel: "o", stem: "売", kana: "ろ", ending: "う", reading: "うろう", romaji: "urō", use: "Volitional: let's / I'll (～う)" },
];

export const verbGroupNames = [
    { japanese: "五段動詞", reading: "ごだんどうし", romaji: "godan dōshi", label: "Group 1", nickname: "U-verbs", ends: "Any u-sound: う く ぐ す つ ぬ ぶ む る" },
    { japanese: "一段動詞", reading: "いちだんどうし", romaji: "ichidan dōshi", label: "Group 2", nickname: "Ru-verbs", ends: "る after an い or え sound" },
    { japanese: "不規則動詞", reading: "ふきそくどうし", romaji: "fukisoku dōshi", label: "Group 3", nickname: "Irregular verbs", ends: "Only する and 来る (くる)" },
];

// Godan verbs of every ending on all five steps. The う-ending verb's first
// step is わ, not あ (買わない), which is why it has its own kana list.
export const godanEndings = [
    { ending: "う", word: "買う", reading: "かう", romaji: "kau", stem: "買", kanas: ["わ", "い", "う", "え", "お"] },
    { ending: "く", word: "書く", reading: "かく", romaji: "kaku", stem: "書", kanas: ["か", "き", "く", "け", "こ"] },
    { ending: "ぐ", word: "泳ぐ", reading: "およぐ", romaji: "oyogu", stem: "泳", kanas: ["が", "ぎ", "ぐ", "げ", "ご"] },
    { ending: "す", word: "話す", reading: "はなす", romaji: "hanasu", stem: "話", kanas: ["さ", "し", "す", "せ", "そ"] },
    { ending: "つ", word: "待つ", reading: "まつ", romaji: "matsu", stem: "待", kanas: ["た", "ち", "つ", "て", "と"] },
    { ending: "ぬ", word: "死ぬ", reading: "しぬ", romaji: "shinu", stem: "死", kanas: ["な", "に", "ぬ", "ね", "の"] },
    { ending: "ぶ", word: "遊ぶ", reading: "あそぶ", romaji: "asobu", stem: "遊", kanas: ["ば", "び", "ぶ", "べ", "ぼ"] },
    { ending: "む", word: "住む", reading: "すむ", romaji: "sumu", stem: "住", kanas: ["ま", "み", "む", "め", "も"] },
    { ending: "る", word: "売る", reading: "うる", romaji: "uru", stem: "売", kanas: ["ら", "り", "る", "れ", "ろ"] },
];

// What attaches after the moving kana on each step: ない, ます, (nothing), ば, う.
export const godanStepEndings = ["ない", "ます", "", "ば", "う"];

export const godanVsIchidan = [
    { form: "Plain negative (～ない)", godan: { stem: "売", kana: "ら", ending: "ない", reading: "うらない · uranai" }, ichidan: { stem: "食べ", ending: "ない", reading: "たべない · tabenai" } },
    { form: "Polite form (～ます)", godan: { stem: "売", kana: "り", ending: "ます", reading: "うります · urimasu" }, ichidan: { stem: "食べ", ending: "ます", reading: "たべます · tabemasu" } },
    { form: "Dictionary form", godan: { stem: "売", kana: "る", ending: "", reading: "うる · uru" }, ichidan: { stem: "食べ", ending: "る", reading: "たべる · taberu" } },
    { form: "Conditional (～ば)", godan: { stem: "売", kana: "れ", ending: "ば", reading: "うれば · ureba" }, ichidan: { stem: "食べ", ending: "れば", reading: "たべれば · tabereba" } },
    { form: "Volitional: let's / I'll (～う / ～よう)", godan: { stem: "売", kana: "ろ", ending: "う", reading: "うろう · urō" }, ichidan: { stem: "食べ", ending: "よう", reading: "たべよう · tabeyō" } },
];

export const irregularForms = [
    { form: "Plain negative (～ない)", suru: ["しない", "shinai"], kuru: ["来ない", "こない", "konai"] },
    { form: "Polite form (～ます)", suru: ["します", "shimasu"], kuru: ["来ます", "きます", "kimasu"] },
    { form: "Dictionary form", suru: ["する", "suru"], kuru: ["来る", "くる", "kuru"] },
    { form: "Conditional (～ば)", suru: ["すれば", "sureba"], kuru: ["来れば", "くれば", "kureba"] },
    { form: "Volitional: let's / I'll (～う / ～よう)", suru: ["しよう", "shiyō"], kuru: ["来よう", "こよう", "koyō"] },
];

export const verbGroupRecognition = [
    { rule: "う, く, ぐ, す, つ, ぬ, ぶ or む", group: "Godan - always", why: "Only る verbs can be Ichidan.", examples: "買う (かう), 書く (かく), 泳ぐ (およぐ), 話す (はなす), 待つ (まつ), 遊ぶ (あそぶ), 飲む (のむ)" },
    { rule: "る after an あ, う or お sound", group: "Godan - always", why: "Ichidan verbs only exist after an い or え sound. This is where 売る (うる) belongs.", examples: "売る (うる), 作る (つくる), 分かる (わかる), 乗る (のる)" },
    { rule: "る after an い or え sound", group: "Usually Ichidan", why: "Most are Ichidan, but a short list is Godan - see the exceptions below.", examples: "食べる (たべる), 見る (みる), 起きる (おきる), 寝る (ねる)" },
    { rule: "する or 来る", group: "Irregular", why: "Each conjugates its own way, including every する compound.", examples: "する, 来る (くる), 勉強する (べんきょうする)" },
];

export const godanLookAlikes = [
    { word: "帰る", reading: "かえる", romaji: "kaeru", meaning: "to go home", polite: "帰ります (かえります)" },
    { word: "入る", reading: "はいる", romaji: "hairu", meaning: "to enter", polite: "入ります (はいります)" },
    { word: "走る", reading: "はしる", romaji: "hashiru", meaning: "to run", polite: "走ります (はしります)" },
    { word: "切る", reading: "きる", romaji: "kiru", meaning: "to cut", polite: "切ります (きります)" },
    { word: "知る", reading: "しる", romaji: "shiru", meaning: "to know", polite: "知ります (しります)" },
    { word: "要る", reading: "いる", romaji: "iru", meaning: "to need", polite: "要ります (いります)" },
    { word: "滑る", reading: "すべる", romaji: "suberu", meaning: "to slip", polite: "滑ります (すべります)" },
    { word: "握る", reading: "にぎる", romaji: "nigiru", meaning: "to grip", polite: "握ります (にぎります)" },
];

export const verbSamePairs = [
    { word: "着る", reading: "きる", romaji: "kiru", meaning: "to wear", group: "Ichidan", polite: "着ます (きます, kimasu)" },
    { word: "切る", reading: "きる", romaji: "kiru", meaning: "to cut", group: "Godan", polite: "切ります (きります, kirimasu)" },
    { word: "変える", reading: "かえる", romaji: "kaeru", meaning: "to change", group: "Ichidan", polite: "変えます (かえます, kaemasu)" },
    { word: "帰る", reading: "かえる", romaji: "kaeru", meaning: "to go home", group: "Godan", polite: "帰ります (かえります, kaerimasu)" },
    { word: "居る", reading: "いる", romaji: "iru", meaning: "to be (people and animals) - usually written いる", group: "Ichidan", polite: "います (imasu)" },
    { word: "要る", reading: "いる", romaji: "iru", meaning: "to need", group: "Godan", polite: "要ります (いります, irimasu)" },
];

export const godanTeForms = [
    { change: "う・つ・る → って", word: "売る", romaji: "uru", te: "売って", teReading: "うって", teRomaji: "utte", meaning: "to sell" },
    { change: "う・つ・る → って", word: "買う", romaji: "kau", te: "買って", teReading: "かって", teRomaji: "katte", meaning: "to buy" },
    { change: "う・つ・る → って", word: "待つ", romaji: "matsu", te: "待って", teReading: "まって", teRomaji: "matte", meaning: "to wait" },
    { change: "む・ぶ・ぬ → んで", word: "住む", romaji: "sumu", te: "住んで", teReading: "すんで", teRomaji: "sunde", meaning: "to live (somewhere)" },
    { change: "む・ぶ・ぬ → んで", word: "遊ぶ", romaji: "asobu", te: "遊んで", teReading: "あそんで", teRomaji: "asonde", meaning: "to play" },
    { change: "む・ぶ・ぬ → んで", word: "死ぬ", romaji: "shinu", te: "死んで", teReading: "しんで", teRomaji: "shinde", meaning: "to die" },
    { change: "く → いて", word: "書く", romaji: "kaku", te: "書いて", teReading: "かいて", teRomaji: "kaite", meaning: "to write" },
    { change: "ぐ → いで", word: "泳ぐ", romaji: "oyogu", te: "泳いで", teReading: "およいで", teRomaji: "oyoide", meaning: "to swim" },
    { change: "す → して", word: "話す", romaji: "hanasu", te: "話して", teReading: "はなして", teRomaji: "hanashite", meaning: "to speak" },
    { change: "Exception: く → って", word: "行く", romaji: "iku", te: "行って", teReading: "いって", teRomaji: "itte", meaning: "to go" },
];

// ---- Row builders for the blog posts (cells are language-neutral; only the
// label arrays passed in differ between the English post and its PT-PT twin).

export function godanFiveStepRows(uses: string[]): TableCellValue[][] {
    return godanFiveSteps.map((item, index) => [
        `${item.step} (${item.vowel})`,
        stepCell(item.stem, item.kana, item.ending),
        `${item.reading} · ${item.romaji}`,
        uses[index],
    ]);
}

export function godanEndingRows(): TableCellValue[][] {
    return godanEndings.map((item) => [
        item.ending,
        `${item.word} (${item.reading}, ${item.romaji})`,
        ...item.kanas.map((kana, index) => stepCell(item.stem, kana, godanStepEndings[index])),
    ]);
}

export function godanVsIchidanRows(formLabels: string[]): TableCellValue[][] {
    return godanVsIchidan.map((item, index) => [
        formLabels[index],
        stepCell(item.godan.stem, item.godan.kana, item.godan.ending),
        [item.ichidan.stem, { strong: item.ichidan.ending }],
    ]);
}

export function irregularRows(formLabels: string[]): TableCellValue[][] {
    return irregularForms.map((item, index) => [
        formLabels[index],
        `${item.suru[0]} (${item.suru[1]})`,
        `${item.kuru[0]} (${item.kuru[1]}, ${item.kuru[2]})`,
    ]);
}

export function godanLookAlikeRows(meanings: string[]): TableCellValue[][] {
    return godanLookAlikes.map((item, index) => [
        `${item.word} (${item.reading}, ${item.romaji})`,
        meanings[index],
        item.polite,
    ]);
}

export function verbSamePairRows(meanings: string[]): TableCellValue[][] {
    return verbSamePairs.map((item, index) => [
        `${item.word} (${item.reading}, ${item.romaji})`,
        meanings[index],
        item.group,
        item.polite,
    ]);
}

export function godanTeFormRows(changeLabels: string[], meanings: string[]): TableCellValue[][] {
    return godanTeForms.map((item, index) => [
        changeLabels[index],
        `${item.word} (${item.romaji})`,
        `${item.te} (${item.teReading}, ${item.teRomaji})`,
        meanings[index],
    ]);
}
